import React, { useState, useEffect } from "react";

// Security helpers
const getApiBaseUrl = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL || "";
  if (apiUrl && !apiUrl.startsWith("http://") && !apiUrl.startsWith("https://")) {
    return "";
  }
  return apiUrl;
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("ogestyle_token");
  if (!token) {
    return null;
  }
  return { Authorization: `Bearer ${token}` };
};

/**
 * ServiceRequestModal - Staff can request to be assigned to services
 * Used in staff dashboard to request access to perform services
 */
function ServiceRequestModal({ isOpen, onClose, onSuccess }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedService, setSelectedService] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAvailableServices();
    }
  }, [isOpen]);

  const fetchAvailableServices = async () => {
    setLoading(true);
    setError("");

    try {
      const apiBaseUrl = getApiBaseUrl();
      const headers = getAuthHeaders();
      if (!headers) {
        setError("Authentication required");
        setLoading(false);
        return;
      }

      // Fetch all service templates (business-level)
      const response = await fetch(`${apiBaseUrl}/api/services/templates`, {
        headers,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch services");
      }

      const data = await response.json();
      
      // Fetch staff's assigned services to filter out
      const staffResponse = await fetch(`${apiBaseUrl}/api/staff/my-services`, {
        headers,
      });
      
      let assignedServiceIds = [];
      if (staffResponse.ok) {
        const staffData = await staffResponse.json();
        assignedServiceIds = staffData.services.map(s => s._id);
      }

      // Filter out services already assigned
      const availableServices = data.services.filter(
        s => !assignedServiceIds.includes(s._id)
      );

      setServices(availableServices);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedService) {
      setError("Please select a service");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const apiBaseUrl = getApiBaseUrl();
      const headers = getAuthHeaders();
      if (!headers) {
        throw new Error("Authentication required");
      }

      const response = await fetch(`${apiBaseUrl}/api/services/requests`, {
        method: "POST",
        headers: {
          ...headers,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          serviceId: selectedService,
          requestMessage,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to submit request");
      }

      const data = await response.json();
      onSuccess && onSuccess(data.request);
      onClose();
      
      // Reset form
      setSelectedService("");
      setRequestMessage("");
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Request Service Assignment</h2>
          <button onClick={onClose} style={styles.closeButton}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}

          {loading ? (
            <div style={styles.loading}>Loading available services...</div>
          ) : services.length === 0 ? (
            <div style={styles.empty}>
              No available services to request. You may already be assigned to
              all services.
            </div>
          ) : (
            <>
              <div style={styles.field}>
                <label style={styles.label}>Service *</label>
                <select
                  value={selectedService}
                  onChange={(e) => setSelectedService(e.target.value)}
                  style={styles.select}
                  required
                >
                  <option value="">Select a service...</option>
                  {services.map((service) => (
                    <option key={service._id} value={service._id}>
                      {service.name} - {service.category}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Message (Optional)</label>
                <textarea
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  placeholder="Explain why you'd like to perform this service..."
                  style={styles.textarea}
                  maxLength={500}
                  rows={4}
                />
                <div style={styles.charCount}>
                  {requestMessage.length}/500 characters
                </div>
              </div>

              <div style={styles.buttons}>
                <button
                  type="button"
                  onClick={onClose}
                  style={styles.cancelButton}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.submitButton}
                  disabled={submitting || !selectedService}
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "white",
    borderRadius: "8px",
    width: "90%",
    maxWidth: "500px",
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "20px",
    borderBottom: "1px solid #e5e7eb",
  },
  title: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "600",
    color: "#111827",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "28px",
    color: "#6b7280",
    cursor: "pointer",
    padding: "0",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  form: {
    padding: "20px",
  },
  error: {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    padding: "12px",
    borderRadius: "6px",
    marginBottom: "16px",
  },
  loading: {
    textAlign: "center",
    padding: "40px",
    color: "#6b7280",
  },
  empty: {
    textAlign: "center",
    padding: "40px",
    color: "#6b7280",
  },
  field: {
    marginBottom: "16px",
  },
  label: {
    display: "block",
    marginBottom: "6px",
    fontWeight: "500",
    fontSize: "14px",
    color: "#374151",
  },
  select: {
    width: "100%",
    padding: "10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "14px",
  },
  textarea: {
    width: "100%",
    padding: "10px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "14px",
    resize: "vertical",
    fontFamily: "inherit",
  },
  charCount: {
    textAlign: "right",
    fontSize: "12px",
    color: "#6b7280",
    marginTop: "4px",
  },
  buttons: {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
    marginTop: "24px",
  },
  cancelButton: {
    padding: "10px 20px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    backgroundColor: "white",
    color: "#374151",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
  },
  submitButton: {
    padding: "10px 20px",
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#2563eb",
    color: "white",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
  },
};

export default ServiceRequestModal;
